import { type ChangeEvent, type DragEvent, useRef, useState } from 'react';

import {
  type PartAttachment,
  useAttachments,
  useDeleteAttachment,
  useUploadAttachment,
} from '@/lib/attachments';
import { cn } from '@/lib/cn';

interface DocumentsSectionProps {
  orgSlug: string;
  partId: number;
}

const ACCEPT = 'application/pdf';

/**
 * Datasheet / PDF gallery for a part. Shares the same upload-and-list
 * mechanics as the image gallery but renders the result as a list of
 * file links rather than thumbnails — PDFs don't have a usable preview
 * we'd want to render inline.
 */
export function DocumentsSection({ orgSlug, partId }: DocumentsSectionProps) {
  const list = useAttachments(orgSlug, partId, 'document');
  const upload = useUploadAttachment(orgSlug, partId, 'document');
  const remove = useDeleteAttachment(orgSlug, partId);

  const inputRef = useRef<HTMLInputElement | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);

  const onFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    for (const file of Array.from(files)) {
      try {
        await upload.mutateAsync(file);
      } catch {
        /* error surfaces below */
      }
    }
  };

  const onSelect = (event: ChangeEvent<HTMLInputElement>) => {
    onFiles(event.target.files);
    event.target.value = '';
  };

  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragOver(false);
    onFiles(event.dataTransfer.files);
  };

  return (
    <section>
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-stone-500 dark:text-stone-400">
          Documents
        </h3>
        {!uploadOpen && (
          <button
            type="button"
            onClick={() => setUploadOpen(true)}
            className="text-xs text-forge-700 hover:underline dark:text-forge-400"
          >
            + Upload
          </button>
        )}
      </div>

      {uploadOpen && (
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          className={cn(
            'mb-2 flex items-center justify-center gap-2 rounded-md border border-dashed px-4 py-3 text-xs transition-colors',
            dragOver
              ? 'border-forge-400 bg-forge-50 text-forge-700 dark:bg-stone-800 dark:text-forge-400'
              : 'border-stone-300 bg-stone-50 text-stone-500 dark:border-stone-700 dark:bg-stone-900 dark:text-stone-400',
          )}
        >
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={upload.isPending}
            className="text-forge-700 hover:underline dark:text-forge-400"
          >
            {upload.isPending ? 'Uploading…' : 'Choose a file'}
          </button>
          <span>or drop one here. PDFs only, up to 25 MB.</span>
          <button
            type="button"
            onClick={() => setUploadOpen(false)}
            className="ml-auto text-stone-500 hover:text-stone-700 dark:text-stone-400 dark:hover:text-stone-200"
            aria-label="Cancel upload"
          >
            ✕
          </button>
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPT}
            multiple
            onChange={onSelect}
            className="hidden"
          />
        </div>
      )}

      {upload.isError && (
        <p className="mb-2 text-xs text-red-600">
          {(upload.error as Error).message}
        </p>
      )}

      {list.data && list.data.length > 0 ? (
        <ul className="divide-y divide-stone-100 rounded-md border border-stone-200 dark:divide-stone-800 dark:border-stone-800">
          {list.data.map((doc) => (
            <DocumentRow
              key={doc.id}
              doc={doc}
              onDelete={() => {
                if (confirm(`Delete ${doc.filename}?`)) {
                  remove.mutate(doc.id);
                }
              }}
              deleting={remove.isPending}
            />
          ))}
        </ul>
      ) : (
        <p className="text-xs text-stone-500 dark:text-stone-400">No documents yet.</p>
      )}

      {remove.isError && (
        <p className="mt-2 text-xs text-red-600">
          {(remove.error as Error).message}
        </p>
      )}
    </section>
  );
}

function DocumentRow({
  doc,
  onDelete,
  deleting,
}: {
  doc: PartAttachment;
  onDelete: () => void;
  deleting: boolean;
}) {
  return (
    <li className="flex items-center justify-between gap-3 px-3 py-2">
      <a
        href={doc.file_url}
        target="_blank"
        rel="noopener noreferrer"
        className="flex min-w-0 items-center gap-2 text-sm text-forge-700 hover:underline dark:text-forge-400"
      >
        <span aria-hidden className="text-stone-400 dark:text-stone-500">📄</span>
        <span className="truncate">{doc.filename}</span>
        <span className="shrink-0 text-xs text-stone-400 dark:text-stone-500">
          {prettyBytes(doc.size_bytes)}
        </span>
      </a>
      <button
        type="button"
        onClick={onDelete}
        disabled={deleting}
        className="text-xs text-stone-400 hover:text-red-600 dark:text-stone-500"
      >
        {deleting ? 'Removing…' : 'Remove'}
      </button>
    </li>
  );
}

function prettyBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}
